<template>
	<Row :gutter="[16, 8]">
		<Col :span="18" :offset="0">
			<VerticalLayout>
				<StatsChartTitleWithDescription
					titleKey="stats.spoofing.heatmap.chartTitle"
					descriptionKey="stats.spoofing.heatmap.chartTitleTooltip"
					titleClass="pd-lg-x pd-md-top"
				/>
				<HeatMapChart
					v-if="mountpointsSelected.length > 0 && datesRangeStr != null"
					:isLoading="isLoading"
					:series="heatmapSeries"
					:categories="heatmapCategories"
					colorSettingsMode="exact"
					:colorSettings="colorSettings"
					:noDataLabel="$t('stats.spoofing.heatmap.legend.noData')"
					showLabelNamesInTooltip
					:xAxisLabel="$t('stats.spoofing.heatmap.xAxisLabel')"
				/>
				<EmptyChartFiltersMessage v-else messageKey="stats.spoofing.heatmap.mountpointsPlaceholder" />
			</VerticalLayout>
		</Col>
		<Col :span="6" :offset="0">
			<VerticalLayout>
				<Card :bordered="false">
					<VerticalLayout>
						<BaseStationSelect
							v-model="mountpointsSelected"
							:onUpdate="fetchStatsItems"
							:placeholderKey="'stats.spoofing.heatmap.mountpointsPlaceholder'"
						/>
						<div v-if="sessionStore.debug" class="pd-vertical">
							<span>{{ $t('stats.spoofing.bandLabel') }}</span>
							<Select
								class="width-100"
								v-model:value="bandsSelected"
								mode="multiple"
								:allowClear="true"
								:options="bandOptions"
								@change="fetchStatsItems"
							/>
						</div>
						<div class="pd-vertical">
							<span>{{ $t('stats.spoofing.elevationCutoffLabel') }}</span>
							<Select
								class="width-100"
								v-model:value="elevationCutoff"
								:options="elevationCutoffOptions"
							/>
						</div>
						<div class="pd-vertical">
							<span>{{ $t('stats.spoofing.heatmap.visibilityThresholdLabel') }}</span>
							<Select
								class="width-100"
								v-model:value="visibilityThreshold"
								:options="visibilityThresholdOptions"
							/>
						</div>
						<DateRangeChooser v-model="datesRange" quickPeriod="day1" @change="fetchStatsItems" />
					</VerticalLayout>
				</Card>
			</VerticalLayout>
		</Col>
	</Row>
</template>

<script lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { Row, Col, Card, Select } from 'ant-design-vue';
import { useI18n } from 'vue-i18n';

import Api from '@/api/Api';
import Consts from '@/api/Consts';

import { useSessionStore } from '@/stores/session';
import { useDateRangeAdapter } from '@/utils/useDateRangeAdapter';
import { generateSeries, pointEntryFactoryXYDay } from '@/utils/chartsUtils';
import {
	buildHeatmapChartItems,
	buildHeatmapColorSettings,
	SPOOFING_GNSS_CODES,
} from '@/utils/spoofingVisibilityHeatmapUtils';
import type { NearElevationItem } from '@/utils/spoofingVisibilityHeatmapUtils';

import { datetimeToString } from '@/components/common/dateUtils';

import VerticalLayout from '@/components/common/VerticalLayout.vue';
import DateRangeChooser from '@/components/common/DateRangeChooser.vue';
import BaseStationSelect from '@/components/common/controls/BaseStationSelect.vue';
import StatsChartTitleWithDescription from '@/components/stats/StatsChartTitleWithDescription.vue';
import EmptyChartFiltersMessage from '@/components/charts/EmptyChartFiltersMessage.vue';
import HeatMapChart from '@/components/charts/HeatMapChart.vue';


const ELEVATION_CUTOFF_OPTIONS = [5, 10, 15, 20, 25, 30, 35, 40].map(value => ({
	value,
	label: String(value),
}));

const VISIBILITY_THRESHOLD_OPTIONS = Array.from({ length: 11 }, (_, index) => {
	const value = 50 + index * 5;
	return {
		value,
		label: String(value) + '%',
	};
});

const DEFAULT_VISIBILITY_THRESHOLD = 75;


export default {
	components: {
		Row, Col, Card, Select,
		VerticalLayout, DateRangeChooser, BaseStationSelect,
		StatsChartTitleWithDescription, EmptyChartFiltersMessage, HeatMapChart,
	},
	setup() {
		const { t } = useI18n();
		const sessionStore = useSessionStore();
		const { datesRange, datesRangeStr, datesRangeSafe } = useDateRangeAdapter(sessionStore, 1, 0);
		const mountpointsSelected = ref<string[]>([]);
		const bandsSelected = ref<string[]>(['1', '2', '5']);
		const elevationCutoff = ref(5);
		const visibilityThreshold = ref(DEFAULT_VISIBILITY_THRESHOLD);
		const { apiData, isLoading, doRequest } = Api.useSpoofingNearElevationGet();

		const bandOptions = computed(() => Consts.NEAR_ELEVATION_BANDS.map(band => ({
			value: band,
			label: 'L' + band,
		})));

		const effectiveBands = computed(() => {
			if (sessionStore.debug) {
				return bandsSelected.value;
			}
			return [...Consts.NEAR_ELEVATION_BANDS];
		});

		const fetchStatsItems = () => {
			if (datesRangeStr.value == null || mountpointsSelected.value.length === 0) {
				return;
			}
			doRequest({
				start_date: datesRangeStr.value[0],
				end_date: datesRangeStr.value[1],
				mountpoints: mountpointsSelected.value,
				gnss: [...SPOOFING_GNSS_CODES],
				bands: effectiveBands.value,
			});
		};

		onMounted(() => {
			fetchStatsItems();
		});

		watch(() => sessionStore.debug, () => {
			fetchStatsItems();
		});

		const chartItems = computed(() => buildHeatmapChartItems(
			(apiData.value.items || []) as NearElevationItem[],
			effectiveBands.value,
			elevationCutoff.value,
			visibilityThreshold.value,
		));

		const heatmapSeriesCategories = computed(() => {
			if (datesRangeSafe.value[0] == null || datesRangeSafe.value[1] == null) {
				return { series: [], categories: [] };
			}
			const result = generateSeries({
				items: chartItems.value as unknown as Array<Record<string, unknown>>,
				getSeriesKeyFn: item => String(item.mountpoint),
				getValueFn: item => (typeof item.status === 'number' ? item.status : null),
				seriesStep: apiData.value.period || 5,
				seriesStepUnit: 'minute',
				categoryFormatter: datetimeToString,
				pointEntryFactory: pointEntryFactoryXYDay,
				showNullAsZero: false,
				minDate: new Date(datesRangeSafe.value[0]),
				maxDate: new Date(datesRangeSafe.value[1]),
				forceSeriesKeys: mountpointsSelected.value,
				roundTimeValues: true,
				pushAdditionalFields: ['tooltipRows'],
			});
			return result;
		});

		const heatmapSeries = computed(() => heatmapSeriesCategories.value.series.map(serie => ({
			name: serie.name,
			hidden: serie.hidden,
			data: serie.data.map(point => {
				if (Array.isArray(point)) {
					return { x: point[0], y: point[1], data: [] as Array<{ label: string; value: string }> };
				}
				const tooltipRows = Array.isArray(point.tooltipRows)
					? point.tooltipRows as Array<{ label: string; value: string }>
					: [];
				return {
					x: point.x,
					y: point.y,
					data: tooltipRows,
				};
			}),
		})));

		const heatmapCategories = computed(() => heatmapSeriesCategories.value.categories.map(category => String(category)));

		const colorSettings = computed(() => buildHeatmapColorSettings(
			t('stats.spoofing.heatmap.legend.ok'),
			t('stats.spoofing.heatmap.legend.multi'),
			{
				G: t('stats.spoofing.heatmap.legend.gps'),
				R: t('stats.spoofing.heatmap.legend.glo'),
				E: t('stats.spoofing.heatmap.legend.gal'),
				C: t('stats.spoofing.heatmap.legend.bds'),
			},
			t('stats.spoofing.heatmap.legend.single'),
		));

		return {
			sessionStore,
			isLoading,
			datesRange,
			datesRangeStr,
			mountpointsSelected,
			bandsSelected,
			bandOptions,
			elevationCutoff,
			elevationCutoffOptions: ELEVATION_CUTOFF_OPTIONS,
			visibilityThreshold,
			visibilityThresholdOptions: VISIBILITY_THRESHOLD_OPTIONS,
			fetchStatsItems,
			heatmapSeries,
			heatmapCategories,
			colorSettings,
		};
	},
};
</script>
